<template>
  <v-card class="elevation-0" data-cy="uatDashboard__widget--reported">
    <v-select
      v-model="status"
      :items="statusList"
      label="Status Filter"
      @change="handleFilterChange($event)"
    ></v-select>
    <v-data-table
      :footer-props="footerProps"
      :headers="headers"
      :items="testPlanList"
      :options.sync="options"
      @update:options="updateRowsPerPageConfig"
      data-cy="uatDashboard__widgetTable--reported"
      item-key="releaseNoteId"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <router-link :to="`/pack/${packId}/releaseNote/${item.releaseNoteId}/testPlan/${serviceAreaId}/view`">{{
              item.releaseNoteId
            }}</router-link>
          </td>
          <td>{{ item.status }}</td>
          <td>{{ truncate(item.title) }}</td>
          <td>
            <v-tooltip v-if="item.summary" bottom max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">{{ truncate(stripHtmlTags(item.summary)) }}</div>
              </template>
              <span v-html="item.summary"></span>
            </v-tooltip>
          </td>
          <td>
            <a :href="`https://jira.ochin.org/browse/${item.uatJira}`" target="_blank" rel="noopener noreferrer" title="Opens in new tab">{{ item.uatJira }}</a>
          </td>
          <td>{{ item.reportedDate }}</td>
          <td>{{ truncate(stripHtmlTags(item.reportedComments)) }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import testPlanApi from "@/api/testPlan";
import { truncate, stripHtmlTags } from "@/lib/summaryFormatters";

export default {
  computed: {
    packId() {
      return this.$route.params.packId;
    },
    rowsPerPage() {
      return this.$store.state.session.user.userConfiguration.widgets.uatDashboardMemberReported.rowsPerPage;
    },
    serviceAreaId() {
      return this.$store.state.session.serviceArea;
    },
    testingStatusFilter() {
      return this.$store.state.session.user.userConfiguration.widgets.uatDashboardMemberReported.filters.testingStatus;
    },
    userConfig() {
      return this.$store.state.session.user.userConfiguration;
    },
  },
  created() {
    // The rowsPerPage computed property is retrieved from the user config.
    this.options.itemsPerPage = this.rowsPerPage;
    this.status = this.testingStatusFilter;
    this.updateTestPlanList();
  },
  data() {
    return {
      footerProps: {
        "items-per-page-options": [5, 10, 25, 50],
      },
      status: "none",
      statusList: [
        { text: "None", value: "none" },
        { text: "In Process", value: "inProcess" },
        { text: "Not Started", value: "notStarted" },
        { text: "Passed/Reviewed", value: "passedReviewed" },
        { text: "Failed/Question", value: "failedQuestion" },
      ],
      headers: [
        { text: "Release Note ID", value: "releaseNoteId" },
        { text: "Status", value: "status" },
        { text: "Title", value: "title" },
        { text: "Summary", value: "summary" },
        { text: "UAT Jira", value: "uatJira" },
        { text: "Date Reported", value: "reportedDate" },
        { text: "Reported Comments", value: "reportedComments" },
      ],
      options: {
        page: 1,
        itemsPerPage: 5,
      },
      testPlanList: [],
    };
  },
  methods: {
    truncate,
    stripHtmlTags,
    async handleFilterChange(val) {
      await this.updateTestingStatusFilterConfig(val);
      await this.updateTestPlanList();
    },
    async updateRowsPerPageConfig(val) {
      // The user config saves to "rowsPerPage", which is what "itemsPerPage" was called in Vuetify v1.
      this.options.itemsPerPage = val.itemsPerPage;
      const widgets = this.userConfig.widgets;
      widgets.uatDashboardMemberReported.rowsPerPage = val.itemsPerPage;
      await this.$store.dispatch("session/updateUserConfiguration", {
        widgets,
      });
    },
    async updateTestingStatusFilterConfig(val) {
      const widgets = this.userConfig.widgets;
      widgets.uatDashboardMemberReported.filters.testingStatus = val;
      await this.$store.dispatch("session/updateUserConfiguration", {
        widgets,
      });
    },
    async updateTestPlanList() {
      const assignedTo = this.$store.state.session.username;
      const { packId, serviceAreaId } = this;

      const statement = {
        operator: "AND",
        list: [
          {
            comparator: "=",
            term: "assignedTo",
            value: assignedTo,
          },
          {
            comparator: "=",
            term: "packId",
            value: packId,
          },
          {
            comparator: "=",
            term: "serviceAreaId",
            value: serviceAreaId,
          },
          {
            comparator: "=",
            term: "reported",
            value: 1,
          },
        ],
      };

      if (this.status === "inProcess") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "In Process",
            },
          ],
        });
      }

      if (this.status === "notStarted") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Not Started",
            },
          ],
        });
      }

      if (this.status === "passedReviewed") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Passed",
            },
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Reviewed",
            },
          ],
        });
      }

      if (this.status === "failedQuestion") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Failed",
            },
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Question",
            },
          ],
        });
      }

      const response = await testPlanApi.search(statement);

      this.testPlanList = response.data.result;
    },
  },
  watch: {
    packId() {
      this.updateTestPlanList();
    },
  },
};
</script>
